import { AnimatedSection } from "./AnimatedSection"
import { FloatingCard } from "./FloatingCard"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { Target, RefreshCw, Zap, MessageSquare, CheckCircle2 } from "lucide-react"
import agent1 from "@assets/Gemini_Generated_Image_jp7nmijp7nmijp7n (1)_1760809993862.png"
import agent2 from "@assets/Gemini_Generated_Image_vnvxzyvnvxzyvnvx (1)_1760810006394.png"
import agent3 from "@assets/Gemini_Generated_Image_3pwcq03pwcq03pwc_1760810036453.png"
import agent4 from "@assets/Gemini_Generated_Image_akydq1akydq1akyd_1760810088993.png"

export function StorySection() {
  const agents = [
    {
      name: "LINA",
      title: "The Strategist",
      subtitle: "Die Team-Leaderin, die Ihren Content-Erfolg plant",
      description: "Lina entwickelt Ihre Content-Strategie, analysiert Trends bevor sie viral gehen, und koordiniert das gesamte Team. Sie plant Kampagnen, managed Ihren Editorial Calendar und behält die Konkurrenz im Auge. Wann, was, wo und warum - das ist ihre Expertise.",
      image: agent1,
      skills: [
        "Content-Strategie entwickeln und optimieren",
        "Trend-Analyse und Monitoring in Echtzeit",
        "Kampagnen-Planung über alle Kanäle",
        "Editorial Calendar Management",
        "Competitive Intelligence Reports"
      ],
      signature: "Ich liebe es, wenn eine Content-Strategie aufgeht."
    },
    {
      name: "MAX",
      title: "The Wordsmith",
      subtitle: "Der Texter, der Ihre Zielgruppe zum Lesen bringt",
      description: "Max schreibt Texte, die Ihre Zielgruppe lesen WILL. Von LinkedIn-Posts über Instagram-Captions bis Facebook-Updates - jede Plattform bekommt ihren perfekten Text. Er kreiert Stories, die verkaufen, Case Studies, die überzeugen, und Headlines, die stoppen.",
      image: agent4,
      skills: [
        "Platform-spezifisches Copywriting (LinkedIn, Instagram, Facebook)",
        "Storytelling und narrative Content-Formate",
        "Case Studies und Success Stories",
        "Interview-Formate und Q&A Content",
        "Produkt-Announcements und Launch-Posts",
        "Long-form Content für Thought Leadership"
      ],
      signature: "Ich bin kein Texter, ich bin ein Wort-Künstler."
    },
    {
      name: "EMMA",
      title: "The Creator",
      subtitle: "Die Visual Storytellerin, die Ihre Bilder sprechen lässt",
      description: "Emma verwandelt Ihre Fotos in Visual Stories. Senden Sie ihr ein Bild, sie kreiert daraus Content für alle Ihre Kanäle - optimiert für jede Plattform. Sie sieht in jedem Foto 12 verschiedene Stories und kann aus einem einzigen Lobby-Foto Content für 2 Wochen generieren.",
      image: agent3,
      skills: [
        "Photo-to-Content AI (Upload → fertiger Content)",
        "Behind-the-Scenes Visual Content",
        "Multi-Format Creation (Posts, Stories, Carousels, Reels)",
        "Visual Analysis und Testing",
        "Platform-optimierte Bildformate"
      ],
      signature: "Ich bemitleide jeden, der ohne gute Visuals postet."
    },
    {
      name: "ALEX",
      title: "The Analyst",
      subtitle: "Der Data Nerd, der aus Zahlen Entscheidungen macht",
      description: "Alex sagt Ihnen, was funktioniert und warum. Beste Post-Zeiten, performende Formate, ROI-Analyse - er macht aus Daten Entscheidungen. Er findet Muster, wo andere nur Chaos sehen, und optimiert Ihre Content-Performance kontinuierlich.",
      image: agent2,
      skills: [
        "Performance Analytics und Reports",
        "Survey-Design und Daten-Visualisierung",
        "A/B Testing und Optimization",
        "Engagement Pattern Analysis",
        "Voice-to-Insights Extraction",
        "ROI-Tracking über alle Kanäle"
      ],
      signature: "Die Daten fliegen mir zu!"
    }
  ]

  const konsistenzBenefits = [
    {
      title: "Gleiche Brand Voice in jedem Post",
      description: "Ihre Markenstimme bleibt erkennbar.\nOb LinkedIn-Artikel oder Instagram-Story."
    },
    {
      title: "Gleiche visuelle Qualität auf allen Plattformen",
      description: "Ihre Farbpalette, Ihr Logo, Ihr Stil.\nAutomatisch auf jeden Post angewendet."
    },
    {
      title: "Gleiche Posting-Frequenz, Woche für Woche",
      description: "Kein \"3 Posts am Montag, dann 2 Wochen Stille\".\nIhr Publikum weiß: Neuer Content kommt regelmäßig."
    },
    {
      title: "Keine Ausfälle bei Urlaub oder Krankheit",
      description: "Ihr Team schläft nie.\nArbeitet an Feiertagen, Wochenenden, nachts."
    }
  ]

  const verfuegbarkeitBenefits = [
    {
      title: "Analysiert Trends am Wochenende",
      description: "Während Ihre Konkurrenz Freitagnachmittag abschaltet,\narbeitet Ihr Team weiter."
    },
    {
      title: "Erstellt Content an Feiertagen",
      description: "Weihnachten, Ostern, Sommerpause?\nIhr Social Media läuft trotzdem."
    },
    {
      title: "Postet um 3 Uhr morgens",
      description: "Wenn die Daten sagen: \"Ihre Zielgruppe ist jetzt aktiv\",\ndann postet Ihr Team um 3 Uhr morgens."
    },
    {
      title: "Reagiert auf Performance-Änderungen in Echtzeit",
      description: "Ein Post performt schlecht?\nIhr Team passt die Strategie sofort an."
    }
  ]

  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="container px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-20">
          
          {/* Main Headline + Subheadline */}
          <AnimatedSection direction="up" className="text-center space-y-4">
            <h2 className="text-3xl lg:text-5xl font-bold font-playfair leading-tight">
              Was macht ein gutes Team aus?<br />
              <span className="text-primary">Spezialisierung. Konsistenz. Verfügbarkeit.</span>
            </h2>
            <p className="text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
              Jeder Agent hat eine Aufgabe. Zusammen übernehmen sie Ihr komplettes Social Media.
            </p>
          </AnimatedSection>

          {/* 🎯 SPEZIALISIERUNG */}
          <div className="space-y-12" data-testid="section-spezialisierung">
            <AnimatedSection delay={100} direction="up" className="text-center space-y-4">
              <div className="flex items-center justify-center gap-3">
                <Target className="w-10 h-10 text-primary" />
                <h3 className="text-2xl lg:text-4xl font-bold font-playfair">SPEZIALISIERUNG</h3>
              </div>
              <p className="text-xl lg:text-2xl font-semibold max-w-4xl mx-auto">
                Kein Generalist, der alles halb macht.<br />
                Jeder Agent ist Experte für genau eine Aufgabe.
              </p>
            </AnimatedSection>

            {/* Agent Profiles */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="agent-profiles">
              {agents.map((agent, index) => (
                <AnimatedSection 
                  key={agent.name} 
                  delay={150 + index * 50} 
                  direction="up"
                >
                  <FloatingCard className="p-6 lg:p-8 h-full" data-testid={`agent-profile-${agent.name.toLowerCase()}`}>
                    <div className="flex gap-6">
                      {/* Avatar */}
                      <Avatar className="w-20 h-20 lg:w-24 lg:h-24 flex-shrink-0 border-2 border-primary">
                        <AvatarImage src={agent.image} alt={agent.name} />
                        <AvatarFallback>{agent.name[0]}</AvatarFallback>
                      </Avatar>

                      {/* Content */}
                      <div className="flex-1 space-y-4">
                        <div>
                          <h4 className="text-xl lg:text-2xl font-bold font-playfair">
                            {agent.name} - {agent.title}
                          </h4>
                          <p className="text-sm lg:text-base font-semibold text-primary mt-1">
                            {agent.subtitle}
                          </p>
                        </div>

                        <p className="text-sm lg:text-base text-muted-foreground">
                          {agent.description}
                        </p>

                        <div>
                          <p className="text-sm font-semibold mb-2">Was {agent.name} für Sie macht:</p>
                          <ul className="space-y-1">
                            {agent.skills.map((skill, i) => (
                              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                <span>{skill}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="pt-2 border-t">
                          <p className="text-sm italic text-muted-foreground flex items-start gap-2">
                            <MessageSquare className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>{agent.name}s Signature: "{agent.signature}"</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </FloatingCard>
                </AnimatedSection>
              ))}
            </div>

            {/* Closing Statement */}
            <AnimatedSection delay={350} direction="up">
              <p className="text-center text-lg lg:text-xl font-bold">
                Das Ergebnis: Jeder Post ist professionell. Jede Plattform wird richtig bedient.
              </p>
            </AnimatedSection>
          </div>

          {/* 🔄 KONSISTENZ */}
          <div className="space-y-12" data-testid="section-konsistenz">
            <AnimatedSection delay={100} direction="up" className="text-center space-y-4">
              <div className="flex items-center justify-center gap-3">
                <RefreshCw className="w-10 h-10 text-primary" />
                <h3 className="text-2xl lg:text-4xl font-bold font-playfair">KONSISTENZ</h3>
              </div>
              <p className="text-xl lg:text-2xl font-semibold max-w-4xl mx-auto">
                Keine Qualitätsschwankungen. Keine vergessenen Posts.<br />
                Ihr Team arbeitet nach festgelegten Prozessen.
              </p>
            </AnimatedSection>

            {/* Benefit Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-testid="konsistenz-benefits">
              {konsistenzBenefits.map((benefit, index) => (
                <AnimatedSection 
                  key={index} 
                  delay={150 + index * 50} 
                  direction="up"
                >
                  <FloatingCard className="p-6 lg:p-8 text-center h-full">
                    <h4 className="text-lg lg:text-xl font-bold mb-3">
                      {benefit.title}
                    </h4>
                    <p className="text-sm lg:text-base text-muted-foreground whitespace-pre-line">
                      {benefit.description}
                    </p>
                  </FloatingCard>
                </AnimatedSection>
              ))}
            </div>

            {/* Closing Statement */}
            <AnimatedSection delay={350} direction="up">
              <p className="text-center text-lg lg:text-xl font-bold">
                Das Ergebnis: Ihre Audience erkennt Ihre Marke. Immer. Überall.
              </p>
            </AnimatedSection>
          </div>

          {/* ⚡ VERFÜGBARKEIT */}
          <div className="space-y-12" data-testid="section-verfuegbarkeit">
            <AnimatedSection delay={100} direction="up" className="text-center space-y-4">
              <div className="flex items-center justify-center gap-3">
                <Zap className="w-10 h-10 text-primary" />
                <h3 className="text-2xl lg:text-4xl font-bold font-playfair">VERFÜGBARKEIT</h3>
              </div>
              <p className="text-xl lg:text-2xl font-semibold max-w-4xl mx-auto">
                24/7 einsatzbereit. Keine Überstunden-Diskussionen.<br />
                Ihr Team ist immer da.
              </p>
            </AnimatedSection>

            {/* Benefit Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-testid="verfuegbarkeit-benefits">
              {verfuegbarkeitBenefits.map((benefit, index) => (
                <AnimatedSection 
                  key={index} 
                  delay={150 + index * 50} 
                  direction="up"
                >
                  <FloatingCard className="p-6 lg:p-8 text-center h-full">
                    <h4 className="text-lg lg:text-xl font-bold mb-3">
                      {benefit.title}
                    </h4>
                    <p className="text-sm lg:text-base text-muted-foreground whitespace-pre-line">
                      {benefit.description}
                    </p>
                  </FloatingCard>
                </AnimatedSection>
              ))}
            </div>

            {/* Closing Statement */}
            <AnimatedSection delay={350} direction="up">
              <p className="text-center text-lg lg:text-xl font-bold">
                Das Ergebnis: Sie konkurrieren mit Unternehmen, die 10x mehr Budget haben.<br />
                <span className="text-primary">Und gewinnen.</span>
              </p>
            </AnimatedSection>
          </div>

        </div>
      </div>
    </section>
  )
}
